import { RequireAccess } from '@/components'
import { useJobillaOrgId } from '@/hooks'
import { Button, logger, SettingsHeading, SettingsNav, Tooltip } from '@/ui'
import { attachClosestEdge, type Edge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
	OrganisationPipelineConfigData,
	organisationPipelineConfigShowQueryKey,
	OrganisationPipelineConfigShowQueryResponse,
	useOrganisationPipelineConfigShow,
	useOrganisationPipelineConfigSystemDefaults,
	useOrganisationPipelineConfigUpdate,
} from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { GripVerticalIcon, InfoIcon, LockIcon, PencilIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

export const Route = createFileRoute('/_auth/company/$companyId/settings/campaign-settings')({
	component: RouteComponent,
})

type PipelineStage = OrganisationPipelineConfigData['stages'][number]

function RouteComponent() {
	const { t } = useTranslation('common')

	return (
		<SettingsNav active="campaign-settings">
			<SettingsHeading>{t('settings.campaignSettings.title')}</SettingsHeading>
			<RequireAccess
				permission="organisation:update"
				fallback={
					<p className="text-red-600">
						{t('settings.campaignSettings.noPermission')}
					</p>
				}
			>
				<PipelineSettingsForm />
			</RequireAccess>
		</SettingsNav>
	)
}

function PipelineSettingsForm() {
	const { t } = useTranslation('common')
	const jobillaOrgId = useJobillaOrgId()
	const hasOrgId = Boolean(jobillaOrgId)

	const queryClient = useQueryClient()
	const queryKey = organisationPipelineConfigShowQueryKey(jobillaOrgId)

	const pipelineQuery = useOrganisationPipelineConfigShow(jobillaOrgId, {
		enabled: hasOrgId,
	})

	const systemDefaultsQuery = useOrganisationPipelineConfigSystemDefaults()
	const systemDefaults = useMemo(
		() => systemDefaultsQuery.data?.map(({ id, name, locked }) => ({ id, name, locked })) ?? [],
		[systemDefaultsQuery.data],
	)

	const [localStages, setLocalStages] = useState<PipelineStage[]>([])
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

	useEffect(() => {
		if (hasOrgId) {
			logger.track('org_settings:pipeline_defaults_viewed', {
				data: {
					organisation_id: jobillaOrgId,
				},
			})
		}
	}, [jobillaOrgId, hasOrgId])

	useEffect(() => {
		if (hasUnsavedChanges || systemDefaults.length === 0) {
			return
		}

		const data = pipelineQuery.data
		if (data?.stages && data.stages.length > 0) {
			if (data.isCustomized) {
				setLocalStages(data.stages)
			} else {
				setLocalStages(systemDefaults)
			}
			setHasUnsavedChanges(false)
		} else if (pipelineQuery.isSuccess) {
			setLocalStages(systemDefaults)
			setHasUnsavedChanges(false)
		}
	}, [pipelineQuery.data, pipelineQuery.isSuccess, hasUnsavedChanges, systemDefaults])

	useEffect(() => {
		if (pipelineQuery.isError) {
			logger.error(pipelineQuery.error, {
				category: 'pipeline',
				data: {
					orgId: jobillaOrgId,
					queryKey,
				},
			})
		}
	}, [pipelineQuery.isError, pipelineQuery.error, jobillaOrgId, queryKey])

	const updateMutation = useOrganisationPipelineConfigUpdate({
		onMutate: (variables) => {
			void queryClient.cancelQueries({ queryKey })
			const snapshot: OrganisationPipelineConfigShowQueryResponse | undefined = queryClient.getQueryData(
				queryKey,
			)
			// The endpoint returns the config unwrapped; writing a { data: ... }
			// envelope here left readers with an undefined `stages` until the
			// mutation settled.
			if (snapshot) {
				queryClient.setQueryData<OrganisationPipelineConfigShowQueryResponse>(queryKey, {
					...snapshot,
					// The request may omit a stage id (the backend mints one); the
					// optimistic cache can only show stages that already have one.
					stages: variables.data.stages.flatMap((stage) =>
						stage.id ? [{ id: stage.id, name: stage.name, locked: stage.locked ?? false }] : []
					),
					isCustomized: true,
				})
			}
			return { snapshot }
		},
		onError: (err: unknown, _data: unknown, ctx: unknown) => {
			const context = ctx as { snapshot?: OrganisationPipelineConfigShowQueryResponse } | undefined

			logger.error(err, {
				category: 'pipeline',
				data: {
					organisationId: jobillaOrgId,
					action: 'update_organisation_pipeline_defaults',
					queryKey,
					hadSnapshot: !!context?.snapshot,
				},
			})

			if (context?.snapshot) {
				queryClient.setQueryData(queryKey, context.snapshot)
			}
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey })
			setHasUnsavedChanges(false)
		},
	})

	const handleStageNameChange = useCallback((stageId: string, newName: string) => {
		setLocalStages((prev) => prev.map((s) => (s.id === stageId ? { ...s, name: newName } : s)))
		setHasUnsavedChanges(true)
	}, [])

	const handleDeleteStage = useCallback((stageId: string) => {
		setLocalStages((prev) => prev.filter((s) => s.id !== stageId))
		setHasUnsavedChanges(true)
	}, [])

	const handleAddStage = useCallback((afterStageId: string) => {
		const newStage: PipelineStage = {
			id: v4(),
			name: t('settings.campaignSettings.newStage'),
			locked: false,
		}
		setLocalStages((prev) => {
			const index = prev.findIndex((s) => s.id === afterStageId)
			if (index === -1) {
				return prev
			}
			const newStages = [...prev]
			newStages.splice(index + 1, 0, newStage)
			return newStages
		})
		setHasUnsavedChanges(true)
	}, [t])

	const handleReorder = useCallback((sourceId: string, targetId: string, edge: Edge) => {
		setLocalStages((prev) => {
			const sourceIndex = prev.findIndex((s) => s.id === sourceId)
			const targetIndex = prev.findIndex((s) => s.id === targetId)
			if (sourceIndex === -1 || targetIndex === -1) {
				return prev
			}

			const sourceStage = prev[sourceIndex]
			if (sourceStage.locked) {
				return prev
			}

			const newStages = [...prev]
			newStages.splice(sourceIndex, 1)

			let insertIndex = targetIndex
			if (sourceIndex < targetIndex) {
				insertIndex = targetIndex - 1
			}
			if (edge === 'bottom') {
				insertIndex += 1
			}

			const newStageIndex = newStages.findIndex((s) => s.name === 'New')
			const hiredIndex = newStages.findIndex((s) => s.name === 'Hired')
			const rejectedIndex = newStages.findIndex((s) => s.name === 'Rejected')

			if (insertIndex <= newStageIndex) {
				insertIndex = newStageIndex + 1
			}
			if (hiredIndex !== -1 && insertIndex > hiredIndex) {
				insertIndex = hiredIndex
			}
			if (rejectedIndex !== -1 && insertIndex > rejectedIndex) {
				insertIndex = rejectedIndex
			}

			newStages.splice(insertIndex, 0, sourceStage)
			return newStages
		})
		setHasUnsavedChanges(true)
	}, [])

	const handleResetToDefaults = useCallback(() => {
		if (!hasOrgId) { return }
		if (systemDefaults.length > 0) {
			setLocalStages(systemDefaults)
			setHasUnsavedChanges(true)
		}
	}, [systemDefaults, hasOrgId])

	const handleSave = useCallback(() => {
		if (!hasOrgId) { return }
		const customStageCount = localStages.filter(s => !s.locked).length
		logger.track('org_settings:pipeline_defaults_updated', {
			data: {
				organisation_id: jobillaOrgId,
				total_custom_stages: customStageCount,
				stage_names: localStages.map(s => s.name),
			},
		})
		updateMutation.mutate({
			organisation: jobillaOrgId,
			data: { stages: localStages },
		})
	}, [updateMutation, localStages, jobillaOrgId, hasOrgId])

	return (
		<div className="space-y-8 max-w-[600px]">
			<section>
				<h2 className="text-xl font-semibold mb-4">
					{t('settings.campaignSettings.pipelineTitle')}
				</h2>
				<p className="text-grey-600 mb-6">
					{t('settings.campaignSettings.pipelineDescription')}
				</p>
				{pipelineQuery.isLoading
					? (
						<div className="flex items-center justify-center py-8 text-grey-500">
							{t('settings.campaignSettings.loading')}
						</div>
					)
					: pipelineQuery.isError
					? (
						<div className="flex flex-col items-center justify-center py-8 text-red-600 gap-2">
							<p>{t('settings.campaignSettings.errorLoad')}</p>
							<button
								type="button"
								onClick={() => pipelineQuery.refetch()}
								className="text-sm text-brand-600 hover:text-brand-700 underline"
							>
								{t('common.tryAgain')}
							</button>
						</div>
					)
					: (
						<StageList
							stages={localStages}
							onNameChange={handleStageNameChange}
							onDelete={handleDeleteStage}
							onAdd={handleAddStage}
							onReorder={handleReorder}
						/>
					)}
			</section>
			<div className="flex justify-between items-center pt-4 border-t border-grey-200">
				<Button
					type="subtle"
					onClick={handleResetToDefaults}
					disabled={!hasOrgId || pipelineQuery.isLoading || pipelineQuery.isError
						|| updateMutation.isPending}
				>
					<RotateCcwIcon className="size-4 mr-2" />
					{t('settings.campaignSettings.resetToDefaults')}
				</Button>
				<Button
					type="committal"
					onClick={handleSave}
					disabled={!hasOrgId || !hasUnsavedChanges || pipelineQuery.isLoading
						|| pipelineQuery.isError
						|| updateMutation.isPending}
					isLoading={updateMutation.isPending}
				>
					{t('settings.campaignSettings.saveChanges')}
				</Button>
			</div>
			{updateMutation.isError
				? (
					<p className="text-red-600 text-sm">
						{t('settings.campaignSettings.errorSave')}
					</p>
				)
				: null}
			<InfoBanner />
		</div>
	)
}

function InfoBanner() {
	const { t } = useTranslation('common')
	return (
		<div className="flex gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
			<InfoIcon className="size-5 text-blue-600 shrink-0 mt-0.5" />
			<div className="text-sm text-blue-800">
				<p className="font-medium">{t('settings.campaignSettings.infoBannerTitle')}</p>
				<p className="mt-1 text-blue-700">
					{t('settings.campaignSettings.infoBannerDescription')}
				</p>
			</div>
		</div>
	)
}

type StageListProps = {
	stages: PipelineStage[]
	onNameChange: (stageId: string, newName: string) => void
	onDelete: (stageId: string) => void
	onAdd: (afterStageId: string) => void
	onReorder: (sourceId: string, targetId: string, edge: Edge) => void
}

function StageList({ stages, onNameChange, onDelete, onAdd, onReorder }: StageListProps) {
	const { t } = useTranslation('common')
	const [draggedId, setDraggedId] = useState<string | null>(null)

	useEffect(() => {
		return monitorForElements({
			onDragStart: ({ source }) => {
				const sourceId = source.data.stageId as string
				setDraggedId(sourceId)
			},
			onDrop: ({ source, location }) => {
				setDraggedId(null)

				const target = location.current.dropTargets[0]
				if (!target) {
					return
				}

				const sourceId = source.data.stageId as string
				const targetId = target.data.stageId as string
				const edge = extractClosestEdge(target.data)

				if (sourceId !== targetId && edge) {
					onReorder(sourceId, targetId, edge)
				}
			},
		})
	}, [onReorder])

	return (
		<div className="space-y-1">
			{stages.map((stage, index) => {
				const isFirst = index === 0
				const isLast = index === stages.length - 1
				const isBeforeHired = stages[index + 1]?.name === 'Hired'
				const canAddAfter = !stage.locked && !isLast && !isBeforeHired

				return (
					<div key={stage.id}>
						<StageItem
							stage={stage}
							onNameChange={onNameChange}
							onDelete={onDelete}
							isDragging={draggedId === stage.id}
							canDrag={!stage.locked && !isFirst && !isLast}
						/>
						{canAddAfter
							? (
								<AddStageButton
									label={t('settings.campaignSettings.addStage')}
									onClick={() => onAdd(stage.id)}
								/>
							)
							: null}
					</div>
				)
			})}
		</div>
	)
}

type StageItemProps = {
	stage: PipelineStage
	onNameChange: (stageId: string, newName: string) => void
	onDelete: (stageId: string) => void
	isDragging: boolean
	canDrag: boolean
}

function StageItem({ stage, onNameChange, onDelete, isDragging, canDrag }: StageItemProps) {
	const { t } = useTranslation('common')
	const ref = useRef<HTMLDivElement>(null)
	const dragHandleRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const [isEditing, setIsEditing] = useState(false)
	const [editedName, setEditedName] = useState(stage.name)
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

	useEffect(() => {
		setEditedName(stage.name)
	}, [stage.name])

	useEffect(() => {
		const element = dragHandleRef.current
		if (!element || !canDrag) {
			return
		}

		return draggable({
			element,
			getInitialData: () => ({ stageId: stage.id }),
		})
	}, [stage.id, canDrag])

	useEffect(() => {
		const element = ref.current
		if (!element) {
			return
		}

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => source.data.stageId !== stage.id && !stage.locked,
			getData: ({ input, element }) => {
				const data = { stageId: stage.id }
				return attachClosestEdge(data, {
					input,
					element,
					allowedEdges: ['top', 'bottom'],
				})
			},
			onDragEnter: ({ self }) => {
				const edge = extractClosestEdge(self.data)
				setClosestEdge(edge)
			},
			onDrag: ({ self }) => {
				const edge = extractClosestEdge(self.data)
				setClosestEdge(edge)
			},
			onDragLeave: () => setClosestEdge(null),
			onDrop: () => setClosestEdge(null),
		})
	}, [stage.id, stage.locked])

	const handleEditClick = () => {
		setIsEditing(true)
		setTimeout(() => {
			inputRef.current?.focus()
			inputRef.current?.select()
		}, 0)
	}

	const handleBlur = () => {
		setIsEditing(false)
		if (editedName.trim() && editedName !== stage.name) {
			onNameChange(stage.id, editedName.trim())
		} else {
			setEditedName(stage.name)
		}
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			handleBlur()
		} else if (e.key === 'Escape') {
			setEditedName(stage.name)
			setIsEditing(false)
		}
	}

	return (
		<div
			ref={ref}
			className={ctl(`
				flex items-center gap-3 p-3 bg-white border border-grey-200 rounded-lg
				transition-all relative
				${isDragging ? 'opacity-50' : ''}
				${stage.locked ? 'bg-grey-50' : ''}
			`)}
		>
			{closestEdge === 'top' && (
				<div className="absolute left-0 right-0 top-0 h-0.5 bg-brand-500 -translate-y-1" />
			)}
			{closestEdge === 'bottom' && (
				<div className="absolute left-0 right-0 bottom-0 h-0.5 bg-brand-500 translate-y-1" />
			)}

			<div
				ref={dragHandleRef}
				className={ctl(`
					${
					canDrag ? 'cursor-grab active:cursor-grabbing text-grey-400 hover:text-grey-600' : 'text-grey-200'
				}
				`)}
			>
				<GripVerticalIcon className="size-5" />
			</div>

			<div className="flex-1 min-w-0">
				{isEditing
					? (
						<input
							ref={inputRef}
							type="text"
							value={editedName}
							onChange={(e) => setEditedName(e.target.value)}
							onBlur={handleBlur}
							onKeyDown={handleKeyDown}
							className="w-full px-2 py-1 border border-brand-300 rounded outline-none ring-2 ring-brand-200"
						/>
					)
					: <span className="font-medium text-grey-900">{stage.name}</span>}
			</div>

			<div className="flex items-center gap-1">
				{stage.locked
					? (
						<span className="p-2 text-grey-400" title={t('settings.campaignSettings.systemStageTooltip')}>
							<LockIcon className="size-4" />
						</span>
					)
					: (
						<>
							<Tooltip content={t('settings.campaignSettings.editStageName')}>
								<button
									type="button"
									onClick={handleEditClick}
									className="p-2 rounded hover:bg-grey-100 text-grey-500 hover:text-grey-700 transition-colors"
								>
									<PencilIcon className="size-4" />
								</button>
							</Tooltip>
							<Tooltip content={t('settings.campaignSettings.deleteStage')}>
								<button
									type="button"
									onClick={() => onDelete(stage.id)}
									className="p-2 rounded hover:bg-red-50 text-grey-500 hover:text-red-600 transition-colors"
								>
									<Trash2Icon className="size-4" />
								</button>
							</Tooltip>
						</>
					)}
			</div>
		</div>
	)
}

type AddStageButtonProps = {
	label: string
	onClick: () => void
}

function AddStageButton({ label, onClick }: AddStageButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full py-2 flex items-center justify-center gap-2 text-sm text-grey-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
		>
			<PlusIcon className="size-4" />
			{label}
		</button>
	)
}
